import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, MapPin, Building2, Paperclip, FileText, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", address: "", contact: "", isp: "", network_notes: "", access_notes: "", notes: "" };

const FIELDS = [
  { key: "address", label: "Address", icon: MapPin },
  { key: "contact", label: "Site Contact", icon: null },
  { key: "isp", label: "Internet / ISP", icon: null },
  { key: "network_notes", label: "Network Notes", icon: null, multiline: true },
  { key: "access_notes", label: "Access Notes", icon: null, multiline: true },
  { key: "notes", label: "Notes", icon: null, multiline: true },
];

function fmtSize(b) {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SiteDocs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site-docs");
      setItems(data);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load site docs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.organization_id]);

  const openCreate = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (row) => {
    setForm({ name: row.name || "", address: row.address || "", contact: row.contact || "", isp: row.isp || "", network_notes: row.network_notes || "", access_notes: row.access_notes || "", notes: row.notes || "" });
    setEditing(row);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error("Site name is required"); return; }
    setBusy(true);
    try {
      if (editing && editing.id) { await api.put(`/site-docs/${editing.id}`, form); toast.success("Site updated"); }
      else { await api.post("/site-docs", form); toast.success("Site added"); }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!deleteRow) return;
    try { await api.delete(`/site-docs/${deleteRow.id}`); toast.success("Site deleted"); setDeleteRow(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const onPickFile = (siteId) => fileInputs.current[siteId]?.click();

  const onUpload = async (siteId, fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); return; }
    setUploadingId(siteId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/site-docs/${siteId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); if (fileInputs.current[siteId]) fileInputs.current[siteId].value = ""; }
  };

  const onDownload = async (siteId, att) => {
    try {
      const res = await api.get(`/site-docs/${siteId}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Couldn't open file"); }
  };

  const onDeleteAtt = async (siteId, att) => {
    try { await api.delete(`/site-docs/${siteId}/attachments/${att.id}`); toast.success("Attachment removed"); load(); }
    catch { toast.error("Couldn't remove attachment"); }
  };

  return (
    <div data-testid="site-docs-page">
      <PageHeader
        eyebrow="[15] Site Docs"
        title="Site Documentation"
        subtitle="Per-site details and files — network, ISP, access, and attachments for each location."
        actions={isAdmin ? (
          <Button data-testid="add-site-btn" onClick={openCreate} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Site
          </Button>
        ) : null}
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {loading && <div className="font-mono text-xs text-[#7A7A7A] py-10 text-center">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="border border-[#E5E0D5] bg-white px-4 py-16 text-center" data-testid="site-docs-empty">
            <Building2 className="w-8 h-8 mx-auto text-[#C9C2B3] mb-3" strokeWidth={1.25} />
            <div className="font-mono text-xs text-[#7A7A7A] mb-3">No sites documented yet.</div>
            {isAdmin && <button onClick={openCreate} data-testid="empty-add-site-btn" className="font-mono text-xs uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]">+ Add your first site</button>}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="site-docs-grid">
            {items.map((row) => (
              <div key={row.id} data-testid={`site-card-${row.id}`} className="border border-[#E5E0D5] bg-white flex flex-col">
                <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-[#E5E0D5]">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-[#F37B2A] shrink-0" strokeWidth={1.5} />
                    <h3 className="text-[#0F0F0F] font-medium tracking-tight truncate" title={row.name}>{row.name}</h3>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(row)} data-testid={`edit-site-${row.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F]" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDeleteRow(row)} data-testid={`delete-site-${row.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>

                <div className="px-5 py-4 space-y-3 text-sm flex-1">
                  {FIELDS.map((f) => (
                    <div key={f.key}>
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] mb-0.5">{f.label}</div>
                      {row[f.key] ? (
                        <p className={`text-[#3A3A3A] ${f.multiline ? "whitespace-pre-wrap" : ""}`} data-testid={`site-${f.key}-${row.id}`}>{row[f.key]}</p>
                      ) : (
                        <p className="font-mono text-xs text-[#9A9A9A]">—</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Attachments */}
                <div className="px-5 py-4 border-t border-[#E5E0D5] space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">
                      <Paperclip className="w-3.5 h-3.5" /> Attachments ({(row.attachments||[]).length})
                    </div>
                    {isAdmin && (
                      <>
                        <input
                          ref={(el) => (fileInputs.current[row.id] = el)}
                          type="file"
                          data-testid={`file-input-${row.id}`}
                          className="hidden"
                          onChange={(e) => onUpload(row.id, e.target.files)}
                        />
                        <button
                          onClick={() => onPickFile(row.id)}
                          data-testid={`attach-file-${row.id}`}
                          disabled={uploadingId === row.id}
                          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F] disabled:opacity-50"
                        >
                          {uploadingId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                          {uploadingId === row.id ? "Uploading…" : "Attach"}
                        </button>
                      </>
                    )}
                  </div>
                  {(row.attachments||[]).length === 0 ? (
                    <div className="font-mono text-xs text-[#9A9A9A]">No files attached.</div>
                  ) : (
                    <ul className="space-y-1">
                      {(row.attachments||[]).map((att) => (
                        <li key={att.id} data-testid={`attachment-${att.id}`} className="flex items-center gap-2 border border-[#E5E0D5] bg-[#FAF7F0] px-2.5 py-1.5">
                          <FileText className="w-3.5 h-3.5 text-[#7A7A7A] shrink-0" />
                          <button onClick={() => onDownload(row.id, att)} data-testid={`download-attachment-${att.id}`} className="flex-1 min-w-0 text-left text-xs text-[#3A3A3A] hover:text-[#F37B2A] truncate" title={att.original_filename}>
                            {att.original_filename}
                          </button>
                          <span className="font-mono text-[10px] text-[#9A9A9A] shrink-0">{fmtSize(att.size)}</span>
                          <button onClick={() => onDownload(row.id, att)} className="p-1 text-[#7A7A7A] hover:text-[#0F0F0F] shrink-0" title="Open"><Download className="w-3.5 h-3.5" /></button>
                          {isAdmin && (
                            <button onClick={() => onDeleteAtt(row.id, att)} data-testid={`delete-attachment-${att.id}`} className="p-1 text-[#7A7A7A] hover:text-[#B91C1C] shrink-0" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/edit modal */}
      <AlertDialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">{editing && editing.id ? "Edit site" : "Add site"}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">Document a physical site — address, contacts, network, and access details.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="uppercase-label">Site Name *</Label>
              <Input data-testid="site-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Head Office" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" autoFocus />
            </div>
            <div>
              <Label className="uppercase-label">Address</Label>
              <Input data-testid="site-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Site Contact</Label>
              <Input data-testid="site-form-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Name / phone" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Internet / ISP</Label>
              <Input data-testid="site-form-isp" value={form.isp} onChange={(e) => setForm({ ...form, isp: e.target.value })} placeholder="Provider, plan, account #" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Network Notes</Label>
              <Textarea data-testid="site-form-network" value={form.network_notes} onChange={(e) => setForm({ ...form, network_notes: e.target.value })} rows={3} placeholder="IP ranges, gateway, switches, VLANs…" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Access Notes</Label>
              <Textarea data-testid="site-form-access" value={form.access_notes} onChange={(e) => setForm({ ...form, access_notes: e.target.value })} rows={3} placeholder="Alarm codes, keys, parking, entry…" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Notes</Label>
              <Textarea data-testid="site-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything else useful…" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="site-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="site-save-btn" disabled={busy || !form.name.trim()} onClick={(e) => { e.preventDefault(); onSave(); }} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-50">{busy ? "Saving…" : (editing && editing.id ? "Save changes" : "Add")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Delete this site?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">&quot;{deleteRow?.name}&quot; and its attachment references will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-site-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
